import React, { useState } from 'react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { siteConfig } from '../data/mock';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { submitContact } from '../services/api';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '' // Honeypot field
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await submitContact(formData);
      setSubmitted(true);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to send message. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <main className="pt-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-24">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h1 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                Get in Touch
              </h1>
              <p className="text-white/60 text-lg leading-relaxed mb-8">
                Have a question or need assistance? We&apos;re here to help. Reach out and we&apos;ll respond as soon as possible.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#C9A962]/10 flex-shrink-0">
                    <Mail size={20} className="text-[#C9A962]" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Email Support</h3>
                    <a 
                      href={`mailto:${siteConfig.supportEmail}`}
                      className="text-[#C9A962] hover:underline"
                    >
                      {siteConfig.supportEmail}
                    </a>
                    <p className="text-white/40 text-sm mt-1">
                      We typically respond within 24-48 hours
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              {submitted ? (
                <div className="p-8 bg-white/[0.02] border border-[#C9A962]/30 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#C9A962]/10 mx-auto mb-6">
                    <CheckCircle size={32} className="text-[#C9A962]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    Message Sent!
                  </h3>
                  <p className="text-white/60">
                    Thank you for reaching out. We&apos;ll get back to you as soon as possible.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: '', email: '', subject: '', message: '', website: '' });
                    }}
                    className="mt-6 text-[#C9A962] hover:underline text-sm"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <div className="p-4 bg-red-500/10 border border-red-500/30 flex items-start gap-3">
                      <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  {/* Honeypot field - hidden from users */}
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    style={{ display: 'none' }}
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  <div>
                    <label htmlFor="name" className="block text-white text-sm font-medium mb-2">
                      Name
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      minLength={2}
                      maxLength={100}
                      value={formData.name}
                      onChange={handleChange}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#C9A962] focus:ring-[#C9A962]/20 rounded-none h-12"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-white text-sm font-medium mb-2">
                      Email
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#C9A962] focus:ring-[#C9A962]/20 rounded-none h-12"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-white text-sm font-medium mb-2">
                      Subject
                    </label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      required
                      minLength={2}
                      maxLength={200}
                      value={formData.subject}
                      onChange={handleChange}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#C9A962] focus:ring-[#C9A962]/20 rounded-none h-12"
                      placeholder="How can we help?"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-white text-sm font-medium mb-2">
                      Message
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      minLength={10}
                      maxLength={5000}
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#C9A962] focus:ring-[#C9A962]/20 rounded-none resize-none"
                      placeholder="Tell us more about your question or concern..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-[#C9A962] text-black font-medium text-base transition-all duration-400 hover:bg-[#C9A962]/10 hover:text-[#C9A962] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span>Sending...</span>
                    ) : (
                      <>
                        <span>Send Message</span>
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
